import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import {
  PATIENT_PASSWORD_RESET,
  PATIENT_PASSWORD_STORE,
  QUESTIONNAIRE,
} from "@routes/routes";
import { USER_DOT_COM_APP_KEY, USER_DOT_COM_APP_URL } from "@config/userDotCom";
import useAccountForUserDotCom from "@hooks/userDotCom/useAccountForUserDotCom";
import useClinicSubscriptionTypeForUserDotCom from "@hooks/userDotCom/useClinicSubscriptionTypeForUserDotCom";
import { SubscriptionPlanCode } from "@client/resources/CurrentSubscriptionResource";
import { createCivchatConfig } from "@hooks/userDotCom/supportChat";

const EXCLUDED_PATHS = [
  PATIENT_PASSWORD_STORE,
  PATIENT_PASSWORD_RESET,
  QUESTIONNAIRE,
];

const useUserDotComInit = () => {
  const location = useLocation();
  const { account } = useAccountForUserDotCom();
  const { subscriptionType } = useClinicSubscriptionTypeForUserDotCom();
  const widgetLoaded = useRef(false);
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const prevPathRef = useRef<string | null>(null);
  const apiKey = USER_DOT_COM_APP_KEY;
  const url = USER_DOT_COM_APP_URL;

  useEffect(() => {
    const path = location.pathname;

    if (!apiKey || !url) {
      return;
    }

    const removeWidget = () => {
      if (window.UE?.destroy) {
        window.UE.destroy();
      }

      window.UE = undefined;
      window.civchat = undefined;

      if (scriptRef.current && scriptRef.current.parentNode) {
        scriptRef.current.parentNode.removeChild(scriptRef.current);
      }

      widgetLoaded.current = false;
      scriptRef.current = null;
    };

    if (EXCLUDED_PATHS.includes(path)) {
      if (widgetLoaded.current) {
        removeWidget();
      }
      return;
    }

    if (!widgetLoaded.current) {
      window.civchat = createCivchatConfig(apiKey);

      const script = document.createElement("script");
      script.src = url;
      script.async = true;
      document.body.appendChild(script);

      scriptRef.current = script;
      widgetLoaded.current = true;
    }

    return () => {
      if (EXCLUDED_PATHS.includes(location.pathname)) {
        removeWidget();
      }
    };
  }, [location.pathname, apiKey, url]);

  useEffect(() => {
    if (!apiKey || !url || !widgetLoaded.current) {
      return;
    }
    if (EXCLUDED_PATHS.includes(location.pathname)) {
      return;
    }
    if (!window.UE) {
      return;
    }

    if (prevPathRef.current === location.pathname) {
      return;
    }

    if (subscriptionType && subscriptionType !== SubscriptionPlanCode.TRIAL) {
      return;
    }

    prevPathRef.current = location.pathname;
    window.UE.pageHit();
  }, [location.pathname, account?.id, subscriptionType, apiKey, url]);
};

export default useUserDotComInit;
